import React, { useEffect } from "react";
import "./WhiteNavbar.css";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

interface WhiteNavbarProps {
  show: boolean;
  onMobileMenuOpen?: () => void;
}

const WhiteNavbar: React.FC<WhiteNavbarProps> = ({ show, onMobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only initialize when the navbar is shown
    if (!show) return;

    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const menuRoot = document.querySelector('.white-navbar .sc_layouts_menu_nav');
      if (!menuRoot) {
        return;
      }

      const items = menuRoot.querySelectorAll('.menu-item-has-children');

      if (items.length === 0) {
        return;
      }

      initializeMenuLogic(menuRoot, items);
    }, 100);

    const initializeMenuLogic = (menuRoot: Element, items: NodeListOf<Element>) => {

      const hoverTimers = new Map<Element, NodeJS.Timeout>();
      const cleanups: Array<() => void> = [];

      const clearAllHovers = () => {
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
          const timer = hoverTimers.get(item);
          if (timer) {
            clearTimeout(timer);
            hoverTimers.delete(item);
          }
        });
      };

      // Check if element is within menu bounds
      const isWithinMenuBounds = (element: Element, x: number, y: number): boolean => {
        const rect = element.getBoundingClientRect();
        const padding = 5; // Small padding for better UX
        return x >= (rect.left - padding) &&
          x <= (rect.right + padding) &&
          y >= (rect.top - padding) &&
          y <= (rect.bottom + padding);
      };

      // Check if cursor is over menu item or its submenu (including nested submenus)
      const isOverMenuItem = (menuItem: Element, x: number, y: number): boolean => {
        if (isWithinMenuBounds(menuItem, x, y)) return true;

        // Check all submenus (including nested ones)
        const submenus = Array.from(menuItem.querySelectorAll('.sub-menu'));
        for (const submenu of submenus) {
          if (isWithinMenuBounds(submenu, x, y)) return true;
        }

        return false;
      };

      // Mouse enter handler
      const handleMouseEnter = (menuItem: Element) => {
        const el = menuItem as HTMLElement;

        // Clear any existing timer
        const existingTimer = hoverTimers.get(el);
        if (existingTimer) {
          clearTimeout(existingTimer);
          hoverTimers.delete(el);
        }

        // Close other open menus at the same level (but not nested ones)
        const parentUl = el.parentElement;
        if (parentUl && parentUl.classList.contains('sub-menu')) {
          // Only close siblings at the same level, not parent menus
          parentUl.querySelectorAll(':scope > li.menu-item-has-children.sfHover').forEach(sibling => {
            if (sibling !== el) {
              (sibling as HTMLElement).classList.remove('sfHover');
              const siblingTimer = hoverTimers.get(sibling);
              if (siblingTimer) {
                clearTimeout(siblingTimer);
                hoverTimers.delete(sibling);
              }
            }
          });
        } else if (parentUl && !parentUl.classList.contains('sub-menu')) {
          // For top-level menus, close all other top-level menus
          parentUl.querySelectorAll(':scope > li.menu-item-has-children.sfHover').forEach(sibling => {
            if (sibling !== el) {
              (sibling as HTMLElement).classList.remove('sfHover');
              const siblingTimer = hoverTimers.get(sibling);
              if (siblingTimer) {
                clearTimeout(siblingTimer);
                hoverTimers.delete(sibling);
              }
            }
          });
        }

        el.classList.add('sfHover');
      };

      // Mouse leave handler
      const handleMouseLeave = (menuItem: Element) => {
        const el = menuItem as HTMLElement;
        const timer = setTimeout(() => {
          el.classList.remove('sfHover');
          hoverTimers.delete(el);
        }, 300);
        hoverTimers.set(el, timer);
      };

      // Global mouse move handler for better tracking
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const x = e.clientX;
        const y = e.clientY;

        // Check if mouse is over any menu item or its submenus
        let foundHover = false;
        items.forEach(item => {
          if (isOverMenuItem(item, x, y)) {
            foundHover = true;
            handleMouseEnter(item);
          }
        });

        // If not over any menu item, close all
        if (!foundHover) {
          clearAllHovers();
        }
      };

      // Add event listeners to each menu item
      items.forEach(item => {
        const el = item as HTMLElement;

        // Mouse enter
        const mouseEnterHandler = () => handleMouseEnter(item);
        el.addEventListener('mouseenter', mouseEnterHandler);

        // Mouse leave
        const mouseLeaveHandler = () => handleMouseLeave(item);
        el.addEventListener('mouseleave', mouseLeaveHandler);

        // Also handle all submenus (including nested ones)
        const submenus = Array.from(item.querySelectorAll('.sub-menu'));
        submenus.forEach(submenu => {
          submenu.addEventListener('mouseenter', mouseEnterHandler);
          submenu.addEventListener('mouseleave', mouseLeaveHandler);
        });

        // Cleanup
        cleanups.push(() => {
          el.removeEventListener('mouseenter', mouseEnterHandler);
          el.removeEventListener('mouseleave', mouseLeaveHandler);
          submenus.forEach(submenu => {
            submenu.removeEventListener('mouseenter', mouseEnterHandler);
            submenu.removeEventListener('mouseleave', mouseLeaveHandler);
          });
          const timer = hoverTimers.get(item);
          if (timer) clearTimeout(timer);
        });
      });

      // Global mouse move listener
      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
      cleanups.push(() => document.removeEventListener('mousemove', handleGlobalMouseMove));

      // Close on outside click or submenu item click
      const handleClick = (e: MouseEvent) => {
        const target = e.target as Node | null;
        if (!target) return;

        // If clicking outside the menu, close all
        if (!menuRoot.contains(target)) {
          clearAllHovers();
          return;
        }

        // If clicking on a submenu item (Link), close the submenu
        const submenuItem = (target as Element).closest('.sub-menu a, .sub-menu [href]');
        if (submenuItem) {
          clearAllHovers();
          return;
        }
      };
      document.addEventListener('click', handleClick);
      cleanups.push(() => document.removeEventListener('click', handleClick));

      // Handle parent link clicks
      const anchors = menuRoot.querySelectorAll('a[href="#"], .menu-item-has-children > a.sf-with-ul');
      anchors.forEach(a => {
        const handler = (e: Event) => {
          e.preventDefault();
          const li = (a as HTMLElement).closest('.menu-item-has-children') as HTMLElement | null;
          if (!li) return;

          const isOpen = li.classList.contains('sfHover');
          clearAllHovers();

          if (!isOpen) {
            li.classList.add('sfHover');
          }
        };
        a.addEventListener('click', handler);
        cleanups.push(() => a.removeEventListener('click', handler));
      });

      return () => {
        clearAllHovers();
        cleanups.forEach(fn => fn());
      };
    };

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname, show]);

  // Close submenus on every page load and route change
  useEffect(() => {
    const closeAllSubmenus = () => {
      // Close submenus in both main header and white navbar
      const menuSelectors = [
        '.white-navbar .sc_layouts_menu_nav',
        '.sc_layouts_menu_nav',
        '#menu_main'
      ];

      menuSelectors.forEach(selector => {
        const menuRoot = document.querySelector(selector);
        if (!menuRoot) return;

        // Close all submenus
        const items = menuRoot.querySelectorAll('.menu-item-has-children');
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
        });
      });
    };

    // Close immediately when route changes
    closeAllSubmenus();

    // Also close after a small delay to ensure DOM is updated
    const timer = setTimeout(closeAllSubmenus, 50);

    return () => clearTimeout(timer);
  }, [location.pathname, show]);

  // Close submenus on page load/refresh and any navigation
  useEffect(() => {
    const closeAllSubmenus = () => {
      // Close submenus in all possible menu locations
      const menuSelectors = [
        '.white-navbar .sc_layouts_menu_nav',
        '.sc_layouts_menu_nav',
        '#menu_main'
      ];

      menuSelectors.forEach(selector => {
        const menuRoot = document.querySelector(selector);
        if (!menuRoot) return;

        const items = menuRoot.querySelectorAll('.menu-item-has-children');
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
        });
      });
    };

    // Close on page load
    closeAllSubmenus();

    // Close on window focus (when user comes back to tab)
    const handleFocus = () => closeAllSubmenus();
    window.addEventListener('focus', handleFocus);

    // Close on any click that might be navigation
    const handleClick = () => {
      // Small delay to allow navigation to start
      setTimeout(closeAllSubmenus, 10);
    };
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  // Add navigation functionality to menu clicks
  useEffect(() => {
    const menuRoot = document.querySelector('.white-navbar .sc_layouts_menu_nav');
    if (!menuRoot) return;

    const handleMenuClick = (e: Event) => {
      const target = e.target as Element | null;
      if (!target) return;

      // Check if clicked element is a link
      const link = target.closest('a');
      if (link) {
        const href = link.getAttribute('href');

        // Only handle internal links (not external or # links)
        if (href && href !== '#' && !href.startsWith('http') && !href.startsWith('mailto:') && !href.startsWith('tel:')) {
          // Prevent default React Router behavior
          e.preventDefault();
          e.stopPropagation();

          // Close all submenus first
          const items = menuRoot.querySelectorAll('.menu-item-has-children');
          items.forEach(item => {
            (item as HTMLElement).classList.remove('sfHover');
          });

          // Small delay to ensure submenus close before navigation
          setTimeout(() => {
            navigate(href);
          }, 50);
        }
      }
    };

    menuRoot.addEventListener('click', handleMenuClick);

    return () => {
      menuRoot.removeEventListener('click', handleMenuClick);
    };
  }, [navigate]);

  useEffect(() => {
    // Add route-changing class to body to force close submenus via CSS
    document.body.classList.add('route-changing');

    // Close all submenus immediately when route changes
    const closeAllSubmenus = () => {
      // Close submenus in all possible menu locations
      const menuSelectors = [
        '.white-navbar .sc_layouts_menu_nav',
        '.sc_layouts_menu_nav',
        '#menu_main'
      ];

      menuSelectors.forEach(selector => {
        const menuRoot = document.querySelector(selector);
        if (!menuRoot) return;

        const items = menuRoot.querySelectorAll('.menu-item-has-children');
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
        });
      });
    };

    // Close submenus immediately on route change
    closeAllSubmenus();

    // Also close after a small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      closeAllSubmenus();
      // Remove route-changing class after a delay
      document.body.classList.remove('route-changing');
    }, 100);

    // Active state based on route - works for all main menu items and their submenus
    const root = document.querySelector('.white-navbar .sc_layouts_menu_nav');
    if (!root) return;

    // Clear all existing active states
    root.querySelectorAll('.current-menu-item, .current_page_item, .current-menu-ancestor, .current-menu-parent')
      .forEach(el => el.classList.remove('current-menu-item', 'current_page_item', 'current-menu-ancestor', 'current-menu-parent'));

    // Find all links in the menu
    const anchors = Array.from(root.querySelectorAll('a[href]')) as HTMLElement[];

    const match = anchors.find(a => {
      const href = (a as HTMLAnchorElement).getAttribute('href') || '';

      // Special handling for root path
      if (location.pathname === '/' && (href === '/' || href === '')) {
        return true;
      }

      try {
        const url = new URL(href, window.location.origin);
        const matches = url.pathname === location.pathname;
        // if (matches) {
        //   console.log('WhiteNavbar: Matched pathname:', url.pathname);
        // }
        return matches;
      } catch {
        const matches = href === location.pathname;
        // if (matches) {
        //   console.log('WhiteNavbar: Matched direct href:', href);
        // }
        return matches;
      }
    });

    if (match) {
      let li: HTMLElement | null = match.closest('li');
      if (li) {
        li.classList.add('current-menu-item', 'current_page_item');
        let parent = li.parentElement;
        while (parent) {
          const pli = parent.closest('li');
          if (pli) {
            pli.classList.add('current-menu-ancestor', 'current-menu-parent');
            parent = pli.parentElement as HTMLElement | null;
          } else {
            break;
          }
        }
      }
    } else {
      console.error('WhiteNavbar: No match found for', location.pathname);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname, show]);

  // Force active state for home page - multiple approaches for reliability
  useEffect(() => {
    const forceHomeActive = () => {
      if (location.pathname === '/') {
        // Target the 3D Printing menu item specifically
        const homeItems = document.querySelectorAll('.menu-item-home, .menu-item-16827');

        homeItems.forEach(item => {
          (item as HTMLElement).classList.add('current-menu-item', 'current_page_item');

          // Also mark parent menu as active
          let parent = item.parentElement;
          while (parent) {
            const pli = parent.closest('li');
            if (pli) {
              pli.classList.add('current-menu-ancestor', 'current-menu-parent');
              parent = pli.parentElement as HTMLElement | null;
            } else {
              break;
            }
          }
        });
      }
    };

    // Multiple attempts with different timings
    forceHomeActive(); // Immediate
    setTimeout(forceHomeActive, 50);   // Very quick
    setTimeout(forceHomeActive, 100);  // Quick
    setTimeout(forceHomeActive, 300);  // Medium
    setTimeout(forceHomeActive, 500);  // Longer
    setTimeout(forceHomeActive, 1000); // Longest

    // Also run when component becomes visible
    if (show) {
      setTimeout(forceHomeActive, 100);
    }
  }, [location.pathname, show]); // Run on pathname change and when navbar shows

  // Additional fallback - run on window load and DOM ready
  useEffect(() => {
    const handleWindowLoad = () => {
      if (location.pathname === '/') {
        const homeItems = document.querySelectorAll('.menu-item-home, .menu-item-16827');
        homeItems.forEach(item => {
          (item as HTMLElement).classList.add('current-menu-item', 'current_page_item');
        });
      }
    };

    // Run on window load
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Also run when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleWindowLoad);
    } else {
      handleWindowLoad();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
      document.removeEventListener('DOMContentLoaded', handleWindowLoad);
    };
  }, [location.pathname]);

  return (
    <header className={`white-navbar ${show ? 'show' : ''}`}>
      <div className="navbar-container">
        {/* Left side - Logo + Desktop menu */}
        <div className="navbar-left">
          <div
            className="sc_layouts_item elementor-element elementor-element-cd0074c sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_logo trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
            data-id="cd0074c"
            data-element_type="widget"
            data-widget_type="trx_sc_layouts_logo.default"
          >
            <div className="elementor-widget-container">
              <Link
                to="/"
                className="sc_layouts_logo sc_layouts_logo_default trx_addons_inline_1884545146 modli-logo-link"
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <img
                  loading="lazy"
                  src="/favicon-modli.png"
                  alt="Modli 3D Icon"
                  width="35"
                  height="35"
                  style={{ display: 'block', height: '3em' }}
                />
                <span
                  className="modli-logo-text"
                  style={{
                    fontSize: '23px',
                    fontWeight: '800',
                    color: '#1c212a',
                    letterSpacing: '0.5px',
                    fontFamily: "'Red Hat Display', sans-serif"
                  }}
                >
                  MODLI 3D
                </span>
              </Link>
            </div>
          </div>

          <div
            className="sc_layouts_item elementor-element elementor-element-e322593 sc_layouts_hide_on_tablet sc_layouts_hide_on_mobile sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_menu trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
            data-id="e322593"
            data-element_type="widget"
            data-widget_type="trx_sc_layouts_menu.default"
          >
            <div className="elementor-widget-container">
              <nav
                className="sc_layouts_menu sc_layouts_menu_default sc_layouts_menu_dir_horizontal menu_hover_zoom_line hide_on_mobile inited inited_kbd"
                data-animation-in="fadeIn"
                data-animation-out="fadeOut"
              >
                <ul
                  id="sc_layouts_menu_699367679"
                  className="sc_layouts_menu_nav inited_cache inited sf-js-enabled sf-arrows"
                  style={{ touchAction: "pan-y" }}
                >
                  <li className="menu-item current-menu-item">
                    <Link to="/"><span>Home</span></Link>
                  </li>
                  <li
                    id="menu-item-17651"
                    className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-17651"
                  >
                    <Link to="/about-us"><span>About Us</span></Link>
                  </li>
                  <li id="menu-item-18008"
                    className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-18008">
                    <Link to="/shop" className="sf-with-ul"><span>Shop</span></Link>
                  </li>
                  <li
                    id="mobile-menu-item-16830"
                    className="menu-item menu-item-type-post_type menu-item-object-page mobile-menu-item-16830"
                  >
                    <Link to="/contact"><span>Contact Us</span></Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        {/* Right side - Hamburger (mobile/tablet) */}
        <div className="navbar-right">
          <div className="sc_layouts_item elementor-element elementor-element-b5cfee9-white sc_layouts_hide_on_wide sc_layouts_hide_on_desktop sc_layouts_hide_on_notebook sc_layouts_hide_on_laptop sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_menu trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
            data-id="b5cfee9-white" data-element_type="widget"
            data-widget_type="trx_sc_layouts_menu.default">
            <div className="elementor-widget-container">
              <div className="sc_layouts_iconed_text sc_layouts_menu_mobile_button_burger sc_layouts_menu_mobile_button without_menu">
                <a className="sc_layouts_item_link sc_layouts_iconed_text_link inited_click"
                  href="/#" role="button" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onMobileMenuOpen?.();
                  }}>
                  <span className="sc_layouts_item_icon sc_layouts_iconed_text_icon trx_addons_icon-menu"></span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default WhiteNavbar;
